// Tweakpane bound to live CONFIG values. Toggle with backtick.
// NOTE: tweakpane v4 ships with incomplete public type defs (the @tweakpane/core
// peer types aren't bundled), so we cast Pane to `any` for the binding/folder API.
import { Pane } from 'tweakpane';
import { CONFIG } from '../config';
import { getSpaceshipAssets } from '../render/SpaceshipAssets';
import type { LocalTransport } from '../net/LocalTransport';
import type { Input } from '../core/Input';
import type { Renderer } from '../render/Renderer';

export class DebugPanel {
  private pane: any;
  private container: HTMLElement;
  private input?: Input;

  constructor(transport: LocalTransport, input?: Input, renderer?: Renderer) {
    this.container = document.createElement('div');
    Object.assign(this.container.style, {
      position: 'fixed',
      top: '0',
      right: '0',
      zIndex: '40',
      width: '300px',
      maxHeight: '100vh',
      overflowY: 'auto',
      // Hidden by default — toggled with backtick. Release-input (Escape) also
      // doesn't automatically open the panel; it just frees the cursor.
      display: 'none'
    });
    document.body.appendChild(this.container);

    this.pane = new Pane({ container: this.container, title: 'Debug (` to toggle)' });
    this.input = input;

    // Diagnostics (slow-motion + collider wireframes + torque kill switch)
    const diag = this.pane.addFolder({ title: 'Diagnostics', expanded: true });
    diag.addBinding(CONFIG.sim, 'simSpeed', { min: 0.005, max: 1, step: 0.005, label: 'Sim speed (slow-mo)' });
    diag.addBinding(CONFIG.sim, 'showWireframes', { label: 'Show colliders & joints' });
    diag.addBinding(CONFIG.sim, 'showHealthBars', { label: 'Show HP / KO bars' });
    diag.addBinding(CONFIG.sim, 'disableTorques', { label: 'Disable PD torques' });
    diag.addBinding(CONFIG.sim, 'unrestrictFps', { label: 'Unrestrict FPS (no VSync)' });
    diag.addBinding(CONFIG.sim, 'debugSeeThroughSaturn', { label: 'See through Saturn' });
    diag.addBinding(CONFIG.sim, 'saturnOrangeTint', { min: 0, max: 1, step: 0.01, label: 'Saturn orange tint' });

    // Ship-map diagnostics — visible mesh triangle count vs heightfield
    // collider triangle count. Only present when the spaceship map loaded
    // its assets; other maps have no equivalent stat.
    const shipAssets = getSpaceshipAssets();
    if (shipAssets) {
      const stats = {
        shipTris: shipAssets.modelTriCount,
        colliderTris: shipAssets.colliderTriCount
      };
      diag.addBinding(stats, 'shipTris', { readonly: true, label: 'Ship tris' });
      diag.addBinding(stats, 'colliderTris', { readonly: true, label: 'Collider tris' });
    }

    diag.addButton({ title: 'Reset round' }).on('click', () => transport.forceReset());
    diag.addButton({ title: 'Pause sim (set speed=0)' }).on('click', () => { CONFIG.sim.simSpeed = 0; this.pane.refresh(); });
    diag.addButton({ title: 'Resume sim (1×)' }).on('click', () => { CONFIG.sim.simSpeed = 1; this.pane.refresh(); });

    const ch = this.pane.addFolder({ title: 'Character motors' });
    ch.addBinding(CONFIG.character, 'standKp', { min: 10, max: 200 });
    ch.addBinding(CONFIG.character, 'standKd', { min: 0, max: 30 });
    ch.addBinding(CONFIG.character, 'standTargetHeight', { min: 0.6, max: 1.4 });
    ch.addBinding(CONFIG.character, 'spineStiff', { min: 0, max: 200 });
    ch.addBinding(CONFIG.character, 'hipStiff', { min: 0, max: 200 });
    ch.addBinding(CONFIG.character, 'shoulderStiff', { min: 0, max: 200 });
    ch.addBinding(CONFIG.character, 'moveAccel', { min: 0, max: 40 });
    ch.addBinding(CONFIG.character, 'swingStiff', { min: 50, max: 800 });

    const walk = this.pane.addFolder({ title: 'Walking', expanded: true });
    walk.addBinding(CONFIG.character, 'footSlotOffset', { min: 0.1, max: 0.6, step: 0.01 });
    walk.addBinding(CONFIG.character, 'standTargetHeight', { min: 0.8, max: 1.5, step: 0.01 });
    walk.addBinding(CONFIG.character, 'pelvisAboveFoot', { min: 0.3, max: 1.2, step: 0.01 });
    walk.addBinding(CONFIG.character, 'hipIkStiff', { min: 0, max: 800 });
    walk.addBinding(CONFIG.character, 'hipIkDamp', { min: 0, max: 80 });
    walk.addBinding(CONFIG.character, 'stanceWidth', { min: 0.05, max: 0.4, step: 0.01 });
    walk.addBinding(CONFIG.character, 'footGaitStride', { min: 0.15, max: 1.0, step: 0.01, label: 'Stride m/flip' });
    walk.addBinding(CONFIG.character, 'footMoveThreshold', { min: 0.05, max: 1.0, step: 0.01, label: 'Move thresh m/s' });
    walk.addBinding(CONFIG.character, 'stepLiftHeight', { min: 0.02, max: 0.5, step: 0.01 });
    walk.addBinding(CONFIG.character, 'footTargetK', { min: 400, max: 3000, step: 50 });
    walk.addBinding(CONFIG.character, 'footTargetD', { min: 20, max: 200, step: 5 });
    walk.addBinding(CONFIG.character, 'maxStepUpHeight', { min: 0.2, max: 2.0, step: 0.01 });
    walk.addBinding(CONFIG.character, 'pelvisFollowK', { min: 0, max: 300 });
    walk.addBinding(CONFIG.character, 'pelvisFollowD', { min: 0, max: 80 });
    walk.addBinding(CONFIG.character, 'jumpImpulse', { min: 4, max: 30 });
    walk.addBinding(CONFIG.character, 'hipAccel', { min: 4, max: 120, step: 2, label: 'Hip accel (1/s)' });
    walk.addBinding(CONFIG.character, 'hipDecel', { min: 4, max: 120, step: 2, label: 'Hip decel (1/s)' });
    walk.addBinding(CONFIG.character, 'hipPlantK', { min: 0, max: 500, step: 5, label: 'Idle plant Kp' });
    walk.addBinding(CONFIG.character, 'hipPlantD', { min: 0, max: 80, step: 1, label: 'Idle plant Kd' });
    walk.addBinding(CONFIG.character, 'armSwingFwd', { min: 0, max: 3.2, step: 0.05, label: 'Arm fwd (walk)' });
    walk.addBinding(CONFIG.character, 'armSwingBack', { min: 0, max: 3.2, step: 0.05, label: 'Arm back (walk)' });
    walk.addBinding(CONFIG.character, 'armSwingFwdSprint', { min: 0, max: 3.2, step: 0.05, label: 'Arm fwd (sprint)' });
    walk.addBinding(CONFIG.character, 'armSwingBackSprint', { min: 0, max: 3.2, step: 0.05, label: 'Arm back (sprint)' });
    walk.addBinding(CONFIG.character, 'armSwingStiff', { min: 100, max: 1200, step: 10, label: 'Arm stiff (walk)' });
    walk.addBinding(CONFIG.character, 'armSwingDamp', { min: 10, max: 100, step: 1, label: 'Arm damp (walk)' });
    walk.addBinding(CONFIG.character, 'armSwingStiffSprint', { min: 80, max: 1000, step: 10, label: 'Arm stiff (sprint)' });
    walk.addBinding(CONFIG.character, 'armSwingDampSprint', { min: 8, max: 90, step: 1, label: 'Arm damp (sprint)' });
    walk.addBinding(CONFIG.character, 'armFlayAngle', { min: 0, max: 0.9, step: 0.01, label: 'Arm flay (out)' });
    walk.addBinding(CONFIG.character, 'legFlayAngle', { min: 0, max: 0.6, step: 0.01, label: 'Leg flay (walk)' });
    walk.addBinding(CONFIG.character, 'legFlaySprintAngle', { min: 0, max: 0.4, step: 0.01, label: 'Leg flay (sprint)' });
    walk.addBinding(CONFIG.character, 'footFlayOutAmount', { min: 0, max: 0.3, step: 0.005, label: 'Foot flay (out, m)' });
    walk.addBinding(CONFIG.character, 'airKneeAngle', { min: -0.05, max: 0.6, step: 0.01, label: 'Airborne knee' });
    walk.addBinding(CONFIG.character, 'airLegHipK', { min: 0, max: 600 });
    walk.addBinding(CONFIG.character, 'airLegHipD', { min: 0, max: 80 });
    walk.addBinding(CONFIG.character, 'airControlAccel', { min: 0, max: 15, step: 0.1, label: 'Air control accel' });
    walk.addBinding(CONFIG.character, 'airFootForward', { min: 0, max: 1.5, step: 0.01, label: 'Jump foot forward' });
    walk.addBinding(CONFIG.character, 'airFootReachUp', { min: 0.2, max: 2.5, step: 0.01, label: 'Jump foot reach up' });
    walk.addBinding(CONFIG.character, 'airFootSpringK', { min: 0, max: 1500 });
    walk.addBinding(CONFIG.character, 'airFootSpringD', { min: 0, max: 120 });
    walk.addBinding(CONFIG.character, 'airFootPullDownScale', { min: 0, max: 1, step: 0.01, label: 'Drag pull-down ×' });

    const head = this.pane.addFolder({ title: 'Head' });
    head.addBinding(CONFIG.character, 'headPullBackward', { label: 'Flip to backward' });
    head.addBinding(CONFIG.character, 'torsoStiff', { min: 100, max: 1500, step: 10, label: 'Torso stiffness' });
    head.addBinding(CONFIG.character, 'torsoDamp', { min: 10, max: 120, step: 1, label: 'Torso damping' });
    head.addBinding(CONFIG.character, 'headForwardPull', { min: 0, max: 20, step: 0.1, label: 'Forward pull (walk)' });
    head.addBinding(CONFIG.character, 'headForwardPullSprintMul', { min: 1, max: 4, step: 0.1, label: 'Forward pull (sprint ×)' });
    head.addBinding(CONFIG.character, 'torsoSprintLean', { min: 0, max: 0.5, step: 0.01, label: 'Torso lean (sprint)' });
    head.addBinding(CONFIG.character, 'headKickAngleDeg', { min: 2, max: 60, step: 1, label: 'Kick trigger (deg)' });
    head.addBinding(CONFIG.character, 'headKickWalkDv', { min: 0, max: 8, step: 0.1, label: 'Kick Δv (walk)' });
    head.addBinding(CONFIG.character, 'headKickRunDv', { min: 0, max: 12, step: 0.1, label: 'Kick Δv (run)' });
    head.addBinding(CONFIG.character, 'neckStiff', { min: 0, max: 300, step: 1, label: 'Neck stiffness' });
    head.addBinding(CONFIG.character, 'neckDamp', { min: 0, max: 60, step: 0.5, label: 'Neck damping' });
    head.addBinding(CONFIG.character, 'torsoNeckAnchorY', { min: 0.2, max: 0.6, step: 0.005, label: 'Neck anchor Y' });

    const combat = this.pane.addFolder({ title: 'Combat' });
    combat.addBinding(CONFIG.character, 'punchCooldownMs', { min: 0, max: 800, step: 10 });
    combat.addBinding(CONFIG.character, 'jumpKickFeetDv', { min: 0, max: 30, step: 0.5, label: 'Jump-kick feet Δv' });
    combat.addBinding(CONFIG.character, 'jumpKickBodyDv', { min: 0, max: 20, step: 0.5, label: 'Jump-kick body Δv' });
    combat.addBinding(CONFIG.character, 'jumpKickUpDv', { min: 0, max: 10, step: 0.1, label: 'Jump-kick up Δv' });
    combat.addBinding(CONFIG.character, 'jumpKickDurationMs', { min: 80, max: 600, step: 10 });
    combat.addBinding(CONFIG.character, 'impactImpulseScale', { min: 0, max: 2, step: 0.05, label: 'Knockback ×' });

    const hit = this.pane.addFolder({ title: 'Hit registration' });
    hit.addBinding(CONFIG.character, 'hitForceSpeedMin', { min: 4, max: 30, step: 0.5, label: 'Force min (m/s)' });
    hit.addBinding(CONFIG.character, 'hitVelAlignMin', { min: 0, max: 1, step: 0.01, label: 'Align gate' });
    hit.addBinding(CONFIG.character, 'accuracyFloor', { min: 0, max: 0.5, step: 0.01, label: 'Accuracy floor' });
    hit.addBinding(CONFIG.character, 'accuracyAlignWeight', { min: 0, max: 1, step: 0.01, label: 'w: align' });
    hit.addBinding(CONFIG.character, 'accuracySpeedWeight', { min: 0, max: 1, step: 0.01, label: 'w: speed' });
    hit.addBinding(CONFIG.character, 'accuracyFacingWeight', { min: 0, max: 1, step: 0.01, label: 'w: facing' });
    hit.addBinding(CONFIG.character, 'hitIdealSpeed', { min: 6, max: 40, step: 0.5, label: 'Ideal speed' });
    hit.addBinding(CONFIG.character, 'hitStrengthMin', { min: 0, max: 30, step: 0.5, label: 'Strength min' });
    hit.addBinding(CONFIG.character, 'hitStrengthMax', { min: 10, max: 200, step: 1, label: 'Strength max' });
    hit.addBinding(CONFIG.character, 'hitStrengthSpeedK', { min: 0.5, max: 10, step: 0.1, label: 'Strength ·speed K' });
    hit.addBinding(CONFIG.character, 'hitStrengthSpeedOffset', { min: 0, max: 20, step: 0.5, label: 'Strength offset' });
    // Read-only monitors of the last registered hit (for tuning the curves).
    const combatSim = transport.world.combat;
    hit.addBinding(combatSim, 'lastHitAccuracy', { readonly: true, label: 'Last acc.', view: 'graph', min: 0, max: 1 });
    hit.addBinding(combatSim, 'lastHitStrength', { readonly: true, label: 'Last strength', view: 'graph', min: 0, max: 100 });
    hit.addBinding(combatSim, 'lastHitAttackerBone', { readonly: true, label: 'Last bone' });

    const hp = this.pane.addFolder({ title: 'Health / KO curve' });
    hp.addBinding(CONFIG.character, 'hpMax', { min: 20, max: 400, step: 10 });
    hp.addBinding(CONFIG.character, 'hpKOChunk', { min: 0.02, max: 0.5, step: 0.01, label: 'HP loss per KO' });
    hp.addBinding(CONFIG.character, 'hpChipFractionOfKO', { min: 0, max: 0.25, step: 0.005, label: 'HP chip / hit' });
    hp.addBinding(CONFIG.character, 'hpAccuracyThreshold', { min: 0.2, max: 1, step: 0.01, label: 'Full-chip acc.' });
    hp.addBinding(CONFIG.character, 'koRegenPerSec', { min: 0, max: 1, step: 0.01, label: 'KO regen / s' });
    hp.addBinding(CONFIG.character, 'koRegenDelay', { min: 0, max: 2, step: 0.05, label: 'KO regen delay' });
    hp.addBinding(CONFIG.character, 'koChipMin', { min: 0.02, max: 0.5, step: 0.01, label: 'KO chip min' });
    hp.addBinding(CONFIG.character, 'koChipMax', { min: 0.2, max: 1.0, step: 0.01, label: 'KO chip max' });
    hp.addBinding(CONFIG.character, 'koChipExponent', { min: 1.0, max: 8.0, step: 0.1, label: 'KO curve ^' });
    hp.addBinding(CONFIG.character, 'koChipForceRef', { min: 8, max: 40, step: 0.5, label: 'Force ref (m/s)' });
    hp.addBinding(CONFIG.character, 'koChipHeadBonus', { min: 1, max: 3, step: 0.05, label: 'Head hit ×' });
    hp.addBinding(CONFIG.character, 'koStunSec', { min: 0.5, max: 5, step: 0.1, label: 'KO stun (s)' });
    hp.addBinding(CONFIG.character, 'koGracePostStandSec', { min: 0, max: 2, step: 0.05, label: 'Post-stand grace' });

    const hb = this.pane.addFolder({ title: 'Headbutt' });
    hb.addBinding(CONFIG.character, 'headbuttWindupMs', { min: 40, max: 300, step: 10 });
    hb.addBinding(CONFIG.character, 'headbuttFollowMs', { min: 80, max: 500, step: 10 });
    hb.addBinding(CONFIG.character, 'headbuttRecoverMs', { min: 80, max: 600, step: 10 });
    hb.addBinding(CONFIG.character, 'headbuttHeadDv', { min: 0, max: 20, step: 0.5, label: 'Head Δv' });
    hb.addBinding(CONFIG.character, 'headbuttTorsoDv', { min: 0, max: 15, step: 0.5, label: 'Torso Δv' });
    hb.addBinding(CONFIG.character, 'headbuttPelvisDv', { min: 0, max: 10, step: 0.1, label: 'Pelvis Δv' });
    hb.addBinding(CONFIG.character, 'headbuttStaminaCost', { min: 0, max: 0.6, step: 0.01, label: 'Stamina cost' });
    hb.addBinding(CONFIG.character, 'headbuttCooldownMs', { min: 0, max: 1500, step: 10, label: 'Cooldown ms' });

    // Hat folder intentionally omitted here — hat placement is tuned from the
    // join-screen "Dev Mode" panel, where sliders target the currently
    // equipped hat's entry in CONFIG.hats.

    const cam = this.pane.addFolder({ title: 'Camera' });
    cam.addBinding(CONFIG.camera, 'distance', { min: 3, max: 14 });
    cam.addBinding(CONFIG.camera, 'height', { min: 0.5, max: 6 });
    cam.addBinding(CONFIG.camera, 'fov', { min: 30, max: 90 });

    const fx = this.pane.addFolder({ title: 'FX' });
    fx.addBinding(CONFIG.render, 'aaMode', {
      options: { FXAA: 'fxaa', TAAU: 'taau', TRAA: 'traa', Off: 'off' },
      label: 'AA mode'
    });
    // FXAA quality preset. Changing this writes through to the three
    // raw threshold fields so the sliders below reflect the preset.
    // 'ultra' exactly matches the three.js addon's baked defaults.
    fx.addBinding(CONFIG.render, 'fxaaQuality', {
      options: { Low: 'low', Medium: 'medium', High: 'high', Ultra: 'ultra' },
      label: 'FXAA quality'
    }).on('change', (e: { value: 'low' | 'medium' | 'high' | 'ultra' }) => {
      const p = {
        low:    { c: 0.0833, r: 0.166, s: 0.25 },
        medium: { c: 0.0625, r: 0.125, s: 0.50 },
        high:   { c: 0.0312, r: 0.063, s: 0.75 },
        ultra:  { c: 0.0312, r: 0.063, s: 1.00 }
      }[e.value];
      CONFIG.render.fxaaContrastThreshold = p.c;
      CONFIG.render.fxaaRelativeThreshold = p.r;
      CONFIG.render.fxaaSubpixelBlending  = p.s;
      this.pane.refresh();
    });
    // Raw FXAA sliders (advanced) — override the preset live.
    // Contrast: smaller = more pixels processed (more work, better AA).
    fx.addBinding(CONFIG.render, 'fxaaContrastThreshold', { min: 0.01, max: 0.2, step: 0.002, label: 'FXAA contrast' });
    // Relative luma threshold: scales the skip rule by local brightness.
    fx.addBinding(CONFIG.render, 'fxaaRelativeThreshold', { min: 0.02, max: 0.3, step: 0.002, label: 'FXAA relative' });
    // Subpixel blending: 0 = hard edges, 1 = soft. Upstream default is 1.
    fx.addBinding(CONFIG.render, 'fxaaSubpixelBlending',  { min: 0, max: 1, step: 0.05, label: 'FXAA subpixel' });
    // TAAU knobs — only meaningful when aaMode='taau'. Kept visible under
    // all modes so flipping back and forth doesn't reshuffle the layout.
    // Res scale: 1.0 = same-res AA only; 0.5 renders the scene at quarter
    // area and reconstructs up — biggest perf win, but blurrier.
    fx.addBinding(CONFIG.render, 'aaTaauResolutionScale', { min: 0.5, max: 1.0, step: 0.05, label: 'TAAU res scale' });
    // Baseline weight for the current frame in the resolve. Lower =
    // smoother, more history, slower to converge on disoccluded regions.
    fx.addBinding(CONFIG.render, 'aaTaauCurrentFrameWeight', { min: 0.005, max: 0.3, step: 0.005, label: 'TAAU frame weight' });
    // Depth delta at which history is considered invalid. Raise if you
    // see history streak through moving foreground; lower if you see
    // flicker on static geometry near edges.
    fx.addBinding(CONFIG.render, 'aaTaauDepthThreshold', { min: 0.00005, max: 0.005, step: 0.00005, label: 'TAAU depth thresh' });
    // Velocity length (in input pixels) at which history fades to zero.
    // Lower values fight ghosting on fast motion but also lose history
    // during slow camera moves.
    fx.addBinding(CONFIG.render, 'aaTaauMaxVelocityLength', { min: 16, max: 256, step: 8, label: 'TAAU max vel (px)' });
    // RCAS sharpen applied to the TAAU output. Standard companion pass
    // that reclaims most of the softness the temporal filter introduces.
    fx.addBinding(CONFIG.render, 'aaTaauSharpen', { label: 'TAAU sharpen' });
    // Sharpness: 0 = strongest, 2 = pass-through. 0.3-0.6 is typical for
    // recovering TAA softness without ringing.
    fx.addBinding(CONFIG.render, 'aaTaauSharpness', { min: 0, max: 2, step: 0.05, label: 'TAAU sharpness' });
    // TRAA knobs. TRAA is same-res temporal AA (no upscaling); sharper
    // than TAAU in most cases since there's no reconstruction blur.
    fx.addBinding(CONFIG.render, 'aaTraaDepthThreshold',    { min: 0.00005, max: 0.005, step: 0.00005, label: 'TRAA depth thresh' });
    fx.addBinding(CONFIG.render, 'aaTraaMaxVelocityLength', { min: 16, max: 256, step: 8, label: 'TRAA max vel (px)' });
    fx.addBinding(CONFIG.render, 'aaTraaSubpixelCorrection', { label: 'TRAA subpixel corr.' });
    fx.addBinding(CONFIG.fx, 'koSlowMoScale', { min: 0.05, max: 1 });
    fx.addBinding(CONFIG.render, 'bloomStrength', { min: 0, max: 2 });
    fx.addBinding(CONFIG.render, 'saturation', { min: 0, max: 2 });
    fx.addBinding(CONFIG.render, 'vignette', { min: 0, max: 1 });
    fx.addBinding(CONFIG.render, 'motionBlur', { label: 'Motion blur' });
    fx.addBinding(CONFIG.render, 'motionBlurAmount', { min: 0, max: 3, step: 0.05, label: 'Motion blur amount' });
    fx.addBinding(CONFIG.render, 'lut', { label: '3D LUT' });
    fx.addBinding(CONFIG.render, 'lutIntensity', { min: 0, max: 1, step: 0.01, label: 'LUT intensity' });
    fx.addBinding(CONFIG.render, 'dof', { label: 'Depth of field' });
    // Focus auto-tracks the focused/spectated character. Offset nudges the
    // focus plane in front of (−) or behind (+) them in metres.
    fx.addBinding(CONFIG.render, 'dofFocusOffset', { min: -3, max: 3, step: 0.05, label: 'DOF focus offset (m)' });
    fx.addBinding(CONFIG.render, 'dofFocalLength', { min: 0.1, max: 10, step: 0.1, label: 'DOF range (m)' });
    fx.addBinding(CONFIG.render, 'dofBokehScale', { min: 0, max: 4, step: 0.05, label: 'DOF bokeh' });
    fx.addBinding(CONFIG.render, 'anamorphic', { label: 'Anamorphic flares' });
    fx.addBinding(CONFIG.render, 'anamorphicIntensity', { min: 0, max: 4, step: 0.05, label: 'Flare intensity' });
    fx.addBinding(CONFIG.render, 'anamorphicThreshold', { min: 0, max: 2, step: 0.01, label: 'Flare threshold' });
    fx.addBinding(CONFIG.render, 'anamorphicScale', { min: 0, max: 10, step: 0.1, label: 'Flare scale' });
    fx.addBinding(CONFIG.render, 'anamorphicColorR', { min: 0, max: 2, step: 0.01, label: 'Flare R' });
    fx.addBinding(CONFIG.render, 'anamorphicColorG', { min: 0, max: 2, step: 0.01, label: 'Flare G' });
    fx.addBinding(CONFIG.render, 'anamorphicColorB', { min: 0, max: 2, step: 0.01, label: 'Flare B' });

    // Screen-Space Ambient Occlusion (GTAO). Master toggle + denoise toggle
    // rebuild a pipeline variant the next render() call (variant cache key
    // includes both flags). All other knobs are uniform writes — edits show
    // up the same frame. Folder collapsed by default.
    const ssao = this.pane.addFolder({ title: 'Ambient Occlusion (SSAO)', expanded: false });
    ssao.addBinding(CONFIG.render, 'ssao', { label: 'Enabled' });
    // Blend strength of the AO darkening: 0 = AO off-looking, 1 = raw GTAO.
    ssao.addBinding(CONFIG.render, 'ssaoIntensity', { min: 0, max: 1, step: 0.01, label: 'Intensity (mix)' });
    // World-space sampling radius. 0.25–1 m is the practical range.
    ssao.addBinding(CONFIG.render, 'ssaoRadius', { min: 0.05, max: 3, step: 0.01, label: 'Radius (m)' });
    // Constant-thickness occluder assumption. Larger lets AO pass through
    // thinner objects more easily.
    ssao.addBinding(CONFIG.render, 'ssaoThickness', { min: 0.05, max: 10, step: 0.05, label: 'Thickness (m)' });
    // Distance fall-off [0..1]. Lower widens the AO band.
    ssao.addBinding(CONFIG.render, 'ssaoDistanceFallOff', { min: 0, max: 1, step: 0.01, label: 'Distance falloff' });
    // Distance exponent (recommended 1–2). Steepens the falloff curve.
    ssao.addBinding(CONFIG.render, 'ssaoDistanceExponent', { min: 0.1, max: 4, step: 0.05, label: 'Distance exp.' });
    // Final scale on the raw AO factor — >1 deepens, <1 lightens.
    ssao.addBinding(CONFIG.render, 'ssaoScale', { min: 0, max: 4, step: 0.05, label: 'Scale (curve)' });
    // Sample count per pixel. 8 = fast/coarse, 16 = balanced, 32 = clean.
    ssao.addBinding(CONFIG.render, 'ssaoSamples', { min: 4, max: 64, step: 1, label: 'Samples / px' });
    // AO buffer resolution scale. 0.5 = quarter-area for ~4× speed-up
    // with a slight softness cost; combined with the bilateral denoise
    // it usually still looks fine at 0.5.
    ssao.addBinding(CONFIG.render, 'ssaoResolutionScale', { min: 0.25, max: 1.0, step: 0.05, label: 'AO res scale' });
    // Bilateral denoise pass on the AO buffer. Toggle requires a pipeline
    // rebuild (cache-key driven); the radius/phi sliders are live.
    ssao.addBinding(CONFIG.render, 'ssaoDenoise', { label: 'Denoise' });
    ssao.addBinding(CONFIG.render, 'ssaoDenoiseRadius', { min: 1, max: 16, step: 0.5, label: 'Denoise radius' });
    // Phi knobs — bilateral edge-rejection sensitivities. Higher = more
    // aggressive blur (less edge protection); lower = sharper but noisier.
    ssao.addBinding(CONFIG.render, 'ssaoDenoiseLumaPhi',   { min: 0.1, max: 20, step: 0.1, label: 'φ luma' });
    ssao.addBinding(CONFIG.render, 'ssaoDenoiseDepthPhi',  { min: 0.1, max: 20, step: 0.1, label: 'φ depth' });
    ssao.addBinding(CONFIG.render, 'ssaoDenoiseNormalPhi', { min: 0.1, max: 20, step: 0.1, label: 'φ normal' });

    const audio = this.pane.addFolder({ title: 'Audio' });
    audio.addBinding(CONFIG.audio, 'musicVolume', { min: 0, max: 1, step: 0.01, label: 'Music volume' });

    // Ion thrusters (spaceship map). Kept behind `shipAssets` since the
    // emitter is only instantiated on that map — sliders would do nothing
    // on sandbox / boxing rings. Expands are the per-thruster pose folders;
    // the shared look knobs sit at the top so they're immediately reachable.
    if (shipAssets) {
      const ion = this.pane.addFolder({ title: 'Ion thrusters', expanded: false });
      const I = CONFIG.render.ionThrusters;
      ion.addBinding(I, 'intensity',       { min: 0, max: 4, step: 0.01, label: 'Intensity' });
      ion.addBinding(I, 'radius',          { min: 0.02, max: 2.0, step: 0.01, label: 'Ring radius' });
      ion.addBinding(I, 'particleSize',    { min: 0.05, max: 2.0, step: 0.01, label: 'Particle size' });
      ion.addBinding(I, 'windPower',       { min: 0, max: 4, step: 0.01, label: 'Wind power' });
      ion.addBinding(I, 'coneSpread',      { min: 0, max: 3, step: 0.01, label: 'Cone spread' });
      ion.addBinding(I, 'jetSpeed',        { min: 0, max: 25, step: 0.1, label: 'Jet speed' });
      ion.addBinding(I, 'jetSpeedJitter',  { min: 0, max: 1, step: 0.01, label: 'Speed jitter' });
      ion.addBinding(I, 'lifeSeconds',     { min: 0.05, max: 3.0, step: 0.01, label: 'Life (s)' });
      ion.addBinding(I, 'lifeJitter',      { min: 0, max: 1, step: 0.01, label: 'Life jitter' });
      ion.addBinding(I, 'spawnRate',       { min: 0, max: 600, step: 1, label: 'Spawn rate' });
      ion.addBinding(I, 'velocityDamping', { min: 0, max: 4, step: 0.01, label: 'Velocity damp' });
      ion.addBinding(I, 'noiseSpeed',      { min: 0, max: 8, step: 0.01, label: 'Flicker speed' });
      ion.addBinding(I, 'softness',        { min: 0, max: 1, step: 0.01, label: 'Edge softness' });

      const ionColors = ion.addFolder({ title: 'Colours (HDR)', expanded: false });
      for (const [prefix, label] of [
        ['core',  'Core (inner)'],
        ['mid',   'Mid (purple)'],
        ['outer', 'Outer (orange)'],
        ['tip',   'Tip (red)']
      ] as const) {
        ionColors.addBinding(I, `${prefix}R`, { min: 0, max: 4, step: 0.01, label: `${label} R` });
        ionColors.addBinding(I, `${prefix}G`, { min: 0, max: 4, step: 0.01, label: `${label} G` });
        ionColors.addBinding(I, `${prefix}B`, { min: 0, max: 4, step: 0.01, label: `${label} B` });
      }

      for (const [which, cfg] of [['A', I.thrusterA], ['B', I.thrusterB]] as const) {
        const tf = ion.addFolder({ title: `Thruster ${which}`, expanded: false });
        tf.addBinding(cfg, 'posX', { min: -15, max: 15, step: 0.01, label: 'Pos X' });
        tf.addBinding(cfg, 'posY', { min: -10, max: 10, step: 0.01, label: 'Pos Y' });
        tf.addBinding(cfg, 'posZ', { min: -15, max: 15, step: 0.01, label: 'Pos Z' });
        tf.addBinding(cfg, 'rotX', { min: -180, max: 180, step: 0.5, label: 'Rot X (°)' });
        tf.addBinding(cfg, 'rotY', { min: -180, max: 180, step: 0.5, label: 'Rot Y (°)' });
        tf.addBinding(cfg, 'rotZ', { min: -180, max: 180, step: 0.5, label: 'Rot Z (°)' });
      }
    }

    // Image-Based Lighting — drives scene.environment + HemisphereLight.
    // All bindings call renderer.applyIbl() so changes are live without a
    // pipeline rebuild. Skipped entirely if the panel was constructed
    // without a renderer reference (older call sites still work).
    if (renderer) {
      const ibl = this.pane.addFolder({ title: 'Lighting / IBL', expanded: false });
      const refresh = () => renderer.applyIbl();
      ibl.addBinding(CONFIG.render, 'iblMode', {
        label: 'Mode',
        options: {
          'A: Room (default)':  'roomDefault',
          'B: Room (aligned)':  'roomAligned',
          'C: Hemisphere':      'hemisphere',
          'D: HDR':             'hdr',
          'E: Off (no IBL)':    'off'
        }
      }).on('change', (e: { value: string }) => {
        // 'roomAligned' seeds rotation from sunDir; the user can then nudge
        // the slider. Other modes leave rotation alone so flipping back to
        // 'roomDefault' returns to a 0° baseline only on first entry.
        if (e.value === 'roomAligned') {
          CONFIG.render.iblRotationDeg = renderer.getSunAlignedRotationDeg();
        } else if (e.value === 'roomDefault') {
          CONFIG.render.iblRotationDeg = 0;
        }
        this.pane.refresh();
        refresh();
      });
      ibl.addBinding(CONFIG.render, 'iblIntensity',    { min: 0, max: 2, step: 0.01, label: 'Intensity' })
        .on('change', refresh);
      ibl.addBinding(CONFIG.render, 'iblRotationDeg',  { min: -180, max: 180, step: 1, label: 'Rotation (°)' })
        .on('change', refresh);
      ibl.addBinding(CONFIG.render, 'iblHemiSky',      { label: 'Hemi sky' })
        .on('change', refresh);
      ibl.addBinding(CONFIG.render, 'iblHemiGround',   { label: 'Hemi ground' })
        .on('change', refresh);
      ibl.addBinding(CONFIG.render, 'iblHemiIntensity',{ min: 0, max: 3, step: 0.01, label: 'Hemi intensity' })
        .on('change', refresh);
      ibl.addBinding(CONFIG.render, 'iblHdrPath',      { label: 'HDR path' });
      ibl.addButton({ title: 'Reload HDR' }).on('click', () => {
        // Switch to 'hdr' mode and force a fresh fetch. Useful after
        // replacing the file on disk or editing the path field above.
        CONFIG.render.iblMode = 'hdr';
        this.pane.refresh();
        renderer.reloadHdr();
      });
    }

    const ai = this.pane.addFolder({ title: 'AI' });
    const aiOpts = { difficulty: 'easy' as 'passive' | 'easy' | 'normal' | 'hard' };
    ai.addBinding(aiOpts, 'difficulty', {
      options: { passive: 'passive', easy: 'easy', normal: 'normal', hard: 'hard' }
    }).on('change', (e: { value: string }) =>
      transport.setDifficulty(e.value as 'passive' | 'easy' | 'normal' | 'hard')
    );

    addEventListener('keydown', (e) => {
      if (e.code === 'Backquote') this.toggle();
    });
  }

  private setVisible(visible: boolean): void {
    this.container.style.display = visible ? 'block' : 'none';
    // Opening the panel releases input so the cursor is free and sim input
    // is suspended; closing it re-enables input (the user still needs to
    // click the canvas to re-lock the pointer for mouse-look).
    this.input?.setReleased(visible);
  }

  toggle(): void {
    this.setVisible(this.container.style.display === 'none');
  }
}
